import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MapView } from '@/components/Map';
import { toast } from 'sonner';
import { Plus, GripVertical, Trash2, Pencil, Loader2, ArrowRight, ArrowLeft, ArrowLeftRight, Search, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { validCoords } from '@/lib/corridorUtils';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TransitStop {
  id: string;
  name: string;
  display_name: string | null;
  lat: number;
  lng: number;
  is_draft: boolean;
}

interface CorridorStopData {
  stop_id: string;
  stop_name: string;
  display_name: string | null;
  lat: number | null;
  lng: number | null;
  position: number;
  is_off_route: boolean;
  is_draft: boolean;
}

interface Corridor {
  id: string;
  name: string;
  description: string | null;
  direction: string;
  is_active: boolean;
  is_draft: boolean;
  published_at: string | null;
  stops?: CorridorStopData[];
}

interface CorridorStopMembership {
  corridor_id: string;
  stop_id: string;
  position: number;
  is_off_route: boolean;
  is_draft: boolean;
}

interface CorridorBuilderProps {
  corridors: Corridor[];
  stops: TransitStop[];
  onRefresh: () => void;
  onSwitchToStops?: (focusStopId?: string) => void;
}

// Sortable stop row
function SortableStopRow({
  id,
  stop,
  position,
  isOffRoute,
  onToggleOffRoute,
  onRemove,
}: {
  id: string;
  stop: TransitStop;
  position: number;
  isOffRoute: boolean;
  onToggleOffRoute: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 rounded-md border border-gray-700/50 group"
    >
      <button {...attributes} {...listeners} className="cursor-grab text-gray-500 hover:text-gray-300">
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-xs text-gray-500 font-mono w-5">{position}</span>
      <span className="flex-1 text-sm text-gray-200">{stop.name}</span>
      <div className="flex items-center gap-1.5">
        <label className="text-xs text-gray-500 cursor-pointer flex items-center gap-1">
          <Switch
            checked={isOffRoute}
            onCheckedChange={onToggleOffRoute}
            className="scale-75"
          />
          <span className={isOffRoute ? 'text-amber-400' : ''}>off-route</span>
        </label>
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Inner corridor detail component (wrapped in ErrorBoundary)
function CorridorDetail({
  selectedCorridor,
  rawCorridors,
  stops,
  memberships,
  corridors,
  onRefresh,
  onSwitchToStops,
}: {
  selectedCorridor: { id: string; name: string; description: string | null; direction: string; is_active: boolean; is_draft: boolean; published_at: string | null };
  rawCorridors: Corridor[];
  stops: TransitStop[];
  memberships: CorridorStopMembership[];
  corridors: { id: string; name: string; description: string | null; direction: string; is_active: boolean; is_draft: boolean; published_at: string | null }[];
  onRefresh: () => void;
  onSwitchToStops?: (focusStopId?: string) => void;
}) {
  const [localStops, setLocalStops] = useState<{ stop_id: string; is_off_route: boolean }[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', direction: 'forward' });
  const [addStopSearch, setAddStopSearch] = useState('');
  const [addStopOpen, setAddStopOpen] = useState(false);
  const miniMapRef = useRef<google.maps.Map | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const miniMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load corridor stops when selection changes
  useEffect(() => {
    const corStops = memberships
      .filter(m => m.corridor_id === selectedCorridor.id)
      .sort((a, b) => a.position - b.position)
      .map(m => ({ stop_id: m.stop_id, is_off_route: m.is_off_route }));
    setLocalStops(corStops);
    setHasChanges(false);
  }, [selectedCorridor.id, memberships]);

  // Get the raw corridor data with inline lat/lng on stops
  const rawCorridor = rawCorridors.find(c => c.id === selectedCorridor.id);
  const corridorStops = rawCorridor?.stops ?? [];

  // Build drawable stops using inline lat/lng from the RPC
  const drawable = useMemo(() => {
    // Map localStops order to corridor stop data with inline coords
    const ordered = localStops.map(ls => {
      const csData = corridorStops.find(cs => cs.stop_id === ls.stop_id);
      if (!csData) return null;
      return { ...csData, is_off_route: ls.is_off_route };
    }).filter(Boolean) as CorridorStopData[];
    return validCoords(ordered);
  }, [localStops, corridorStops]);

  // Count of stops missing valid coords
  const missingCoordsStops = useMemo(() => {
    return localStops.filter(ls => {
      const csData = corridorStops.find(cs => cs.stop_id === ls.stop_id);
      if (!csData) return true;
      return (
        typeof csData.lat !== 'number' ||
        typeof csData.lng !== 'number' ||
        !Number.isFinite(csData.lat) ||
        !Number.isFinite(csData.lng) ||
        csData.lat < 15.1 || csData.lat > 15.7 ||
        csData.lng < -61.5 || csData.lng > -61.2
      );
    }).map(ls => {
      const csData = corridorStops.find(cs => cs.stop_id === ls.stop_id);
      const stop = stops.find(s => s.id === ls.stop_id);
      return {
        stop_id: ls.stop_id,
        stop_name: csData?.stop_name ?? stop?.name ?? 'Unknown',
      };
    });
  }, [localStops, corridorStops, stops]);

  // Render mini-map with inline coords
  useEffect(() => {
    if (!miniMapRef.current || !window.google) return;

    // Clear
    miniMarkersRef.current.forEach(m => { m.map = null; });
    miniMarkersRef.current = [];
    if (polylineRef.current) { polylineRef.current.setMap(null); polylineRef.current = null; }

    if (drawable.length === 0) return;

    const path: google.maps.LatLngLiteral[] = [];
    const bounds = new google.maps.LatLngBounds();

    drawable.forEach((s, idx) => {
      const pos = { lat: s.lat, lng: s.lng };
      path.push(pos);
      bounds.extend(pos);

      const numEl = document.createElement('div');
      numEl.style.cssText = `
        width: 20px; height: 20px; border-radius: 50%;
        background: ${s.is_off_route ? '#f59e0b' : '#22c55e'};
        border: 2px solid white; display: flex; align-items: center;
        justify-content: center; font-size: 10px; font-weight: bold;
        color: white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      `;
      numEl.textContent = String(s.position + 1);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: miniMapRef.current!,
        position: pos,
        content: numEl,
        title: s.display_name ?? s.stop_name,
      });
      miniMarkersRef.current.push(marker);
    });

    if (path.length > 1) {
      polylineRef.current = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#22c55e',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: miniMapRef.current,
      });
      miniMapRef.current.fitBounds(bounds, 40);
    } else if (path.length === 1) {
      miniMapRef.current.setCenter(path[0]);
      miniMapRef.current.setZoom(14);
    }
  }, [drawable]);

  // Drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localStops.findIndex(s => s.stop_id === active.id);
    const newIndex = localStops.findIndex(s => s.stop_id === over.id);
    setLocalStops(arrayMove(localStops, oldIndex, newIndex));
    setHasChanges(true);
  };

  // Save order
  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const stopsJson = localStops.map(s => ({
        stop_id: s.stop_id,
        is_off_route: s.is_off_route,
      }));
      const { data: setData, error } = await supabase.rpc('admin_set_corridor_stops', {
        p_corridor_id: selectedCorridor.id,
        p_stops: stopsJson,
        p_is_draft: true,
      });
      if (error) throw error;
      const setResult = setData as any;
      if (setResult?.ok === false) throw new Error(setResult.error || 'Failed to save stop order');
      toast.success('Stop order saved as draft');
      setHasChanges(false);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  // Publish corridor
  const handlePublish = async () => {
    setPublishing(true);
    try {
      const { data: pubData, error } = await supabase.rpc('admin_publish_drafts', { p_corridor_id: selectedCorridor.id });
      if (error) throw error;
      const pubResult = pubData as any;
      if (pubResult?.ok === false) throw new Error(pubResult.error || 'Publish failed');
      toast.success('Corridor published. Mobile apps will sync within seconds.');
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Edit corridor
  const handleEdit = async () => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_upsert_corridor', {
        p_id: selectedCorridor.id,
        p_name: editForm.name.trim(),
        p_description: editForm.description.trim() || null,
        p_direction: editForm.direction,
        p_is_draft: true,
      });
      if (error) throw error;
      toast.success('Corridor updated');
      setEditOpen(false);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update corridor');
    } finally {
      setSaving(false);
    }
  };

  // Add stop to corridor
  const handleAddStop = (stopId: string) => {
    if (localStops.some(s => s.stop_id === stopId)) {
      toast.error('Stop already in this corridor');
      return;
    }
    setLocalStops(prev => [...prev, { stop_id: stopId, is_off_route: false }]);
    setHasChanges(true);
    setAddStopOpen(false);
    setAddStopSearch('');
  };

  // Direction icon
  const DirectionIcon = ({ dir }: { dir: string }) => {
    if (dir === 'forward') return <ArrowRight className="w-3.5 h-3.5" />;
    if (dir === 'reverse') return <ArrowLeft className="w-3.5 h-3.5" />;
    return <ArrowLeftRight className="w-3.5 h-3.5" />;
  };

  // Filtered stops for add dropdown
  const availableStops = stops.filter(s => {
    if (!addStopSearch) return true;
    return s.name.toLowerCase().includes(addStopSearch.toLowerCase());
  }).filter(s => !localStops.some(ls => ls.stop_id === s.id));

  // Check if corridor has drafts
  const corridorHasDrafts = () => {
    if (selectedCorridor.is_draft) return true;
    return memberships.some(m => m.corridor_id === selectedCorridor.id && m.is_draft);
  };

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3">
        <h2 className="text-lg font-semibold text-white flex-1">{selectedCorridor.name}</h2>
        <Badge variant="outline" className="border-gray-600 text-gray-400 flex items-center gap-1">
          <DirectionIcon dir={selectedCorridor.direction} />
          {selectedCorridor.direction}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setEditForm({
              name: selectedCorridor.name,
              description: selectedCorridor.description || '',
              direction: selectedCorridor.direction,
            });
            setEditOpen(true);
          }}
          className="text-gray-400 hover:text-white h-8"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          onClick={handlePublish}
          disabled={publishing || !corridorHasDrafts()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
        >
          {publishing && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
          Publish corridor
        </Button>
      </div>

      {/* Missing coords warning */}
      {missingCoordsStops.length > 0 && (
        <div className="mx-4 mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-amber-300 font-medium">
                {missingCoordsStops.length} stop{missingCoordsStops.length > 1 ? 's' : ''} hidden from the map because {missingCoordsStops.length > 1 ? "they don't" : "it doesn't"} have valid GPS yet.
              </p>
              <p className="text-xs text-amber-400/70 mt-1">Open the Stops Map tab to drag-pin them.</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {missingCoordsStops.map(s => (
                  <button
                    key={s.stop_id}
                    onClick={() => onSwitchToStops?.(s.stop_id)}
                    className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-300 border border-amber-500/20 rounded-full hover:bg-amber-500/20 hover:text-amber-200 transition-colors cursor-pointer"
                  >
                    {s.stop_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stop list + mini-map */}
      <div className="flex-1 flex overflow-hidden">
        {/* Stop list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {hasChanges && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-blue-900/20 border border-blue-700/30 rounded-md">
              <span className="text-xs text-blue-400">Unsaved changes</span>
              <Button size="sm" onClick={handleSaveOrder} disabled={saving} className="h-6 text-xs bg-blue-600 hover:bg-blue-700 ml-auto">
                {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Save order
              </Button>
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={localStops.map(s => s.stop_id)} strategy={verticalListSortingStrategy}>
              {localStops.map((ls, idx) => {
                const stop = stops.find(s => s.id === ls.stop_id);
                if (!stop) return null;
                return (
                  <SortableStopRow
                    key={ls.stop_id}
                    id={ls.stop_id}
                    stop={stop}
                    position={idx + 1}
                    isOffRoute={ls.is_off_route}
                    onToggleOffRoute={() => {
                      setLocalStops(prev => prev.map((s, i) =>
                        i === idx ? { ...s, is_off_route: !s.is_off_route } : s
                      ));
                      setHasChanges(true);
                    }}
                    onRemove={() => {
                      setLocalStops(prev => prev.filter((_, i) => i !== idx));
                      setHasChanges(true);
                    }}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          {localStops.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No stops in this corridor yet.</p>
          )}

          {/* Add stop */}
          <div className="relative mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddStopOpen(!addStopOpen)}
              className="w-full border-dashed border-gray-600 text-gray-400 hover:text-white h-9"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add stop
            </Button>
            {addStopOpen && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-gray-900 border border-gray-700 rounded-md shadow-xl z-50 max-h-48 overflow-y-auto">
                <div className="p-2 border-b border-gray-800">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                    <Input
                      value={addStopSearch}
                      onChange={e => setAddStopSearch(e.target.value)}
                      placeholder="Search stops..."
                      className="pl-7 bg-gray-800 border-gray-700 text-white h-7 text-xs"
                      autoFocus
                    />
                  </div>
                </div>
                {availableStops.slice(0, 20).map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleAddStop(s.id)}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800"
                  >
                    {s.name}
                  </button>
                ))}
                {availableStops.length === 0 && (
                  <p className="text-xs text-gray-500 p-3 text-center">No matching stops</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mini-map */}
        <div className="w-72 border-l border-gray-800 flex flex-col">
          <div className="px-3 py-2 border-b border-gray-800">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Route preview</span>
          </div>
          <div className="flex-1">
            <MapView
              className="w-full h-full"
              initialCenter={{ lat: 15.41, lng: -61.37 }}
              initialZoom={11}
              onMapReady={(map) => {
                miniMapRef.current = map;
                map.setOptions({
                  disableDefaultUI: true,
                  zoomControl: true,
                  styles: [
                    { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
                    { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
                    { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#0e1626' }] },
                    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
                  ],
                });
              }}
            />
          </div>
        </div>
      </div>

      {/* Edit corridor modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit corridor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-gray-400 text-xs">Name</Label>
              <Input
                value={editForm.name}
                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Description</Label>
              <Input
                value={editForm.description}
                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Direction</Label>
              <Select value={editForm.direction} onValueChange={v => setEditForm(prev => ({ ...prev, direction: v }))}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forward">Forward</SelectItem>
                  <SelectItem value="reverse">Reverse</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CorridorBuilder({ corridors: rawCorridors, stops, onRefresh, onSwitchToStops }: CorridorBuilderProps) {
  // Derive flat memberships from corridor.stops arrays
  const memberships: CorridorStopMembership[] = rawCorridors.flatMap(c =>
    (c.stops ?? []).map(s => ({
      corridor_id: c.id,
      stop_id: s.stop_id,
      position: s.position,
      is_off_route: s.is_off_route,
      is_draft: s.is_draft,
    }))
  );
  // Strip stops array from corridors to match local Corridor interface
  const corridors = rawCorridors.map(({ stops: _s, ...rest }) => rest);
  const [selectedCorridorId, setSelectedCorridorId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ id: '', name: '', description: '', direction: 'forward' });

  const selectedCorridor = corridors.find(c => c.id === selectedCorridorId);

  // Direction icon
  const DirectionIcon = ({ dir }: { dir: string }) => {
    if (dir === 'forward') return <ArrowRight className="w-3.5 h-3.5" />;
    if (dir === 'reverse') return <ArrowLeft className="w-3.5 h-3.5" />;
    return <ArrowLeftRight className="w-3.5 h-3.5" />;
  };

  // Check if corridor has drafts
  const corridorHasDrafts = (cId: string) => {
    const cor = corridors.find(c => c.id === cId);
    if (cor?.is_draft) return true;
    return memberships.some(m => m.corridor_id === cId && m.is_draft);
  };

  // Create corridor
  const handleCreate = async () => {
    if (!createForm.id.trim() || !createForm.name.trim()) {
      toast.error('ID and name are required');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_upsert_corridor', {
        p_id: createForm.id.trim(),
        p_name: createForm.name.trim(),
        p_description: createForm.description.trim() || null,
        p_direction: createForm.direction,
        p_is_draft: true,
      });
      if (error) throw error;
      toast.success(`Corridor '${createForm.name}' created as draft`);
      setCreateOpen(false);
      setCreateForm({ id: '', name: '', description: '', direction: 'forward' });
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create corridor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex">
      {/* Left pane — corridor list */}
      <div className="w-72 border-r border-gray-800 overflow-y-auto p-3 space-y-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Corridors</h3>
          <Button size="sm" variant="ghost" onClick={() => setCreateOpen(true)} className="h-7 text-emerald-400 hover:text-emerald-300">
            <Plus className="w-3.5 h-3.5 mr-1" /> New
          </Button>
        </div>

        {corridors.map(c => {
          const stopCount = memberships.filter(m => m.corridor_id === c.id).length;
          const hasDrafts = corridorHasDrafts(c.id);
          return (
            <Card
              key={c.id}
              className={`cursor-pointer transition-all border ${
                selectedCorridorId === c.id
                  ? 'bg-emerald-900/20 border-emerald-600/50'
                  : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-600'
              }`}
              onClick={() => setSelectedCorridorId(c.id)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{c.name}</span>
                  <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400">
                    <DirectionIcon dir={c.direction} />
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-gray-500">{stopCount} stops</span>
                  {hasDrafts && (
                    <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-[10px] px-1.5 py-0">draft</Badge>
                  )}
                  {c.published_at && !hasDrafts && (
                    <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 text-[10px] px-1.5 py-0">published</Badge>
                  )}
                </div>
                {c.published_at && (
                  <p className="text-[10px] text-gray-600 mt-1">
                    Published {new Date(c.published_at).toLocaleDateString()}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}

        {corridors.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">No corridors yet. Create one to get started.</p>
        )}
      </div>

      {/* Right pane — selected corridor */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedCorridor ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <p>Select a corridor from the list to edit its stops.</p>
          </div>
        ) : (
          <ErrorBoundary
            fallback={({ error, reset }) => (
              <div className="m-6 rounded-lg border border-red-500/30 bg-red-500/5 p-6">
                <p className="font-medium text-red-400">Could not render this corridor.</p>
                <p className="mt-2 text-sm text-red-300/70">
                  {String(error.message ?? error)}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={reset}
                >
                  Retry
                </Button>
              </div>
            )}
          >
            <CorridorDetail
              key={selectedCorridor.id}
              selectedCorridor={selectedCorridor}
              rawCorridors={rawCorridors}
              stops={stops}
              memberships={memberships}
              corridors={corridors}
              onRefresh={onRefresh}
              onSwitchToStops={onSwitchToStops}
            />
          </ErrorBoundary>
        )}
      </div>

      {/* Create corridor modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>New corridor</DialogTitle>
            <DialogDescription className="text-gray-400">Create a new transit corridor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-gray-400 text-xs">ID (slug)</Label>
              <Input
                value={createForm.id}
                onChange={e => setCreateForm(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                placeholder="e.g. west-coast"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Name</Label>
              <Input
                value={createForm.name}
                onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. West Coast"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Description</Label>
              <Input
                value={createForm.description}
                onChange={e => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Direction</Label>
              <Select value={createForm.direction} onValueChange={v => setCreateForm(prev => ({ ...prev, direction: v }))}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forward">Forward</SelectItem>
                  <SelectItem value="reverse">Reverse</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
